import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import RequireRole from './components/Auth/RequireRole';
import Login from './components/Auth/Login';

// 佈局組件
import AdminLayout from './layouts/AdminLayout';

// 營運端頁面（管理員/營運人員）
import Dashboard from './pages/Dashboard';
import Customers from './pages/Customers';
import Contacts from './pages/Contacts';
import Contracts from './pages/Contracts';
import Quotes from './pages/Quotes';
import ServiceRates from './pages/ServiceRates';
import QuoteTemplates from './pages/QuoteTemplates';
import Settings from './pages/Settings';
import OverseasServiceCalculator from './pages/OverseasServiceCalculator';
import UserManagement from './pages/admin/UserManagement';
import PartnerContracts from './pages/admin/PartnerContracts';
import PartnerApply from './pages/PartnerApply';

// 邀請頁面（僅內部人員）
import Invite from './pages/Invite';

/**
 * AutoRedirect - 自動導向組件（處理登入後的導向）
 */
function AutoRedirect() {
  const { user, userRole, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // 如果正在載入，不執行導向
    if (loading) return;

    // 如果用戶已登入且有角色，且當前在登入頁面，則導向到對應的儀表板
    if (user && userRole && location.pathname === '/login') {
      let targetRoute = '/login';
      if (userRole.role === 'admin' || userRole.role === 'operator') {
        targetRoute = '/admin/dashboard';
      }
      
      if (targetRoute !== '/login') {
        navigate(targetRoute, { replace: true });
      }
    }
    
    // 如果用戶已登入但沒有角色，且當前在登入頁面，顯示提示（不導向）
    if (user && !userRole && !loading && location.pathname === '/login') {
      // 用戶已登入但沒有角色資訊
    }
  }, [user, userRole, loading, location.pathname, navigate]);

  return null;
}

/**
 * AppContent - 應用內容組件（需要在 AuthProvider 內部使用）
 */
function AppContent() {
  const { user, userRole, loading, isAdmin, isOperator } = useAuth();

  // 顯示載入畫面
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-ci-bg">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-ci-text">載入中...</p>
        </div>
      </div>
    );
  }

  // 根據角色自動導向對應的首頁
  const getDefaultRoute = (): string => {
    if (!user || !userRole) return '/login';
    if (isAdmin() || isOperator()) return '/admin/dashboard';
    return '/login';
  };

  // 計算預設路由
  const defaultRoute = getDefaultRoute();

  return (
    <>
      <AutoRedirect />
      <Routes>
        {/* 登入頁面 */}
        <Route path="/login" element={
          user && userRole ? <Navigate to={defaultRoute} replace /> : <Login />
        } />
      
      {/* 承攬夥伴申請頁面（公開，無需登入） */}
      <Route path="/partner/apply" element={<PartnerApply />} />

      {/* 邀請頁面（公開，僅內部人員邀請） */}
      <Route path="/invite" element={<Invite />} />

      {/* 營運端路由（管理員/營運人員） */}
      <Route path="/admin/*" element={
        <RequireRole roles={['admin', 'operator']}>
          <AdminLayout>
            <Routes>
              <Route path="/" element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/customers" element={<Customers />} />
              <Route path="/contacts" element={<Contacts />} />
              <Route path="/projects" element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="/projects/:projectId/items" element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="/contracts" element={<Contracts />} />
              {/* 發票功能已移除，重導至儀表板 */}
              <Route path="/invoices" element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="/partners" element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="/expenses" element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="/quotes" element={<Quotes />} />
              <Route path="/service-rates" element={<ServiceRates />} />
              <Route path="/quote-templates" element={<QuoteTemplates />} />
              <Route path="/customer-deposits" element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/overseas-calculator" element={<OverseasServiceCalculator />} />
              <Route path="/reports" element={<Navigate to="/admin/dashboard" replace />} />
              {/* 使用者管理（僅 admin） */}
              <Route path="/users" element={
                <RequireRole roles="admin">
                  <UserManagement />
                </RequireRole>
              } />
              {/* 承攬合約管理 */}
              <Route path="/partner-contracts" element={<PartnerContracts />} />
            </Routes>
          </AdminLayout>
        </RequireRole>
      } />

      {/* 舊客戶端／夥伴端入口：不再提供外部登入，導向內部登入頁 */}
      <Route path="/customer/*" element={<Navigate to="/login" replace />} />
      <Route path="/partner/*" element={<Navigate to="/login" replace />} />

        {/* 根路徑自動導向 */}
        <Route path="/" element={<Navigate to={defaultRoute} replace />} />

          {/* 舊路由兼容性重定向（暫時保留以支援現有書籤和連結） */}
        <Route path="/dashboard" element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="/customers" element={<Navigate to="/admin/customers" replace />} />
        <Route path="/contacts" element={<Navigate to="/admin/contacts" replace />} />
        <Route path="/projects" element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="/contracts" element={<Navigate to="/admin/contracts" replace />} />
        <Route path="/invoices" element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="/partners" element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="/expenses" element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="/quotes" element={<Navigate to="/admin/quotes" replace />} />
        <Route path="/service-rates" element={<Navigate to="/admin/service-rates" replace />} />
        <Route path="/customer-deposits" element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="/settings" element={<Navigate to="/admin/settings" replace />} />
        <Route path="/overseas-calculator" element={<Navigate to="/admin/overseas-calculator" replace />} />
        <Route path="/reports" element={<Navigate to="/admin/dashboard" replace />} />

        {/* 404 頁面 */}
        <Route path="*" element={<Navigate to={defaultRoute} replace />} />
      </Routes>
    </>
  );
}

/**
 * Agentation 開發工具（僅開發環境載入，production 不打包）
 */
function DevAgentation() {
  const [AgentationComponent, setAgentationComponent] = useState<React.ComponentType | null>(null);

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      import('agentation').then(({ Agentation }) => setAgentationComponent(() => Agentation));
    }
  }, []);

  return AgentationComponent ? <AgentationComponent /> : null;
}

/**
 * App 主組件
 */
function App() {
  return (
    <Router
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true
      }}
    >
      <AuthProvider>
        <NotificationProvider>
          <AppContent />
        </NotificationProvider>
      </AuthProvider>
      <DevAgentation />
    </Router>
  );
}

export default App;
